/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { mealsApi, recipesApi } from '../../services/api';
import type {
  MealEntry,
  AddMealEntryRequest,
  UpdateMealEntryRequest,
  DailyNutritionSummary,
  Recipe,
} from '../../types';

interface MealsState {
  meals: MealEntry[];
  dailySummary: DailyNutritionSummary | null;
  recipes: Recipe[];
  selectedRecipe: Recipe | null;
  isLoading: boolean;
  error: string | null;
  selectedDate: string;
}

const initialState: MealsState = {
  meals: [],
  dailySummary: null,
  recipes: [],
  selectedRecipe: null,
  isLoading: false,
  error: null,
  selectedDate: new Date().toISOString().split('T')[0],
};

// Async thunks
export const getMealsByDate = createAsyncThunk(
  'meals/getMealsByDate',
  async (date: string, { rejectWithValue }) => {
    try {
      const meals = await mealsApi.getMealsByDate(date);
      return meals;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get meals');
    }
  }
);

export const addMealEntry = createAsyncThunk(
  'meals/addMealEntry',
  async (data: AddMealEntryRequest, { rejectWithValue }) => {
    try {
      const meal = await mealsApi.addMealEntry(data);
      return meal;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to add meal');
    }
  }
);

export const updateMealEntry = createAsyncThunk(
  'meals/updateMealEntry',
  async ({ id, data }: { id: string; data: UpdateMealEntryRequest }, { rejectWithValue }) => {
    try {
      const meal = await mealsApi.updateMealEntry(id, data);
      return meal;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to update meal');
    }
  }
);

export const deleteMealEntry = createAsyncThunk(
  'meals/deleteMealEntry',
  async (id: string, { rejectWithValue }) => {
    try {
      await mealsApi.deleteMealEntry(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to delete meal');
    }
  }
);

export const getDailyNutritionSummary = createAsyncThunk(
  'meals/getDailyNutritionSummary',
  async (date: string, { rejectWithValue }) => {
    try {
      const summary = await mealsApi.getDailyNutritionSummary(date);
      return summary;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get daily summary');
    }
  }
);

export const searchRecipes = createAsyncThunk(
  'meals/searchRecipes',
  async (query: string, { rejectWithValue }) => {
    try {
      const result = await recipesApi.searchRecipes(query);
      return result.recipes;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to search recipes');
    }
  }
);

export const getRecipeById = createAsyncThunk(
  'meals/getRecipeById',
  async (id: string, { rejectWithValue }) => {
    try {
      const recipe = await recipesApi.getRecipeById(id);
      return recipe;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get recipe');
    }
  }
);

export const getRecommendedRecipes = createAsyncThunk(
  'meals/getRecommendedRecipes',
  async (_, { rejectWithValue }) => {
    try {
      const recipes = await recipesApi.getRecommendedRecipes();
      return recipes;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get recommended recipes');
    }
  }
);

// Slice
const mealsSlice = createSlice({
  name: 'meals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    clearSelectedRecipe: (state) => {
      state.selectedRecipe = null;
    },
  },
  extraReducers: (builder) => {
    // Get meals by date
    builder.addCase(getMealsByDate.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMealsByDate.fulfilled, (state, action) => {
      state.isLoading = false;
      state.meals = action.payload;
      state.error = null;
    });
    builder.addCase(getMealsByDate.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Add meal entry
    builder.addCase(addMealEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addMealEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.meals.push(action.payload);
      state.error = null;
    });
    builder.addCase(addMealEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update meal entry
    builder.addCase(updateMealEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateMealEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      const index = state.meals.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.meals[index] = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateMealEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete meal entry
    builder.addCase(deleteMealEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteMealEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.meals = state.meals.filter((m) => m.id !== action.payload);
      state.error = null;
    });
    builder.addCase(deleteMealEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get daily nutrition summary
    builder.addCase(getDailyNutritionSummary.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getDailyNutritionSummary.fulfilled, (state, action) => {
      state.isLoading = false;
      state.dailySummary = action.payload;
      state.error = null;
    });
    builder.addCase(getDailyNutritionSummary.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Search recipes
    builder.addCase(searchRecipes.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(searchRecipes.fulfilled, (state, action) => {
      state.isLoading = false;
      state.recipes = action.payload;
      state.error = null;
    });
    builder.addCase(searchRecipes.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get recipe by id
    builder.addCase(getRecipeById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getRecipeById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.selectedRecipe = action.payload;
      state.error = null;
    });
    builder.addCase(getRecipeById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get recommended recipes
    builder.addCase(getRecommendedRecipes.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getRecommendedRecipes.fulfilled, (state, action) => {
      state.isLoading = false;
      state.recipes = action.payload;
      state.error = null;
    });
    builder.addCase(getRecommendedRecipes.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, setSelectedDate, clearSelectedRecipe } = mealsSlice.actions;
export default mealsSlice.reducer;
